import { Box, Flex, Grid, Text } from "@mantine/core";
import { useForm } from "@tanstack/react-form";
import {
  CSSProperties,
  type JSX,
  ReactNode,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import { FormInputCategory, FormInputItem, LooseObject } from "~/utils/Types";
import { Message } from "~/utils/Types";

import Button, { ButtonProps } from "../Button";
import SmallButton from "../Button/SmallButton";
import ServerMessage from "../ServerMessage";
import DatePicker from "./DatePicker";
import DateRangePicker from "./DateRangePicker";
import ValidationSchema from "./ValidationSchema";
import {
  FormAutocomplete,
  FormCheckbox,
  FormRadioGroup,
  FormSelect,
  FormTextField,
} from "./fields";

export const SELECT_ALL_VALUE = "select_all";

const isFieldEmpty = (category: FormInputCategory, value: unknown): boolean => {
  switch (category) {
    case FormInputCategory.CHECK_BOX:
      return !value;
    case FormInputCategory.AUTO_COMPLETE:
      return !(value as { value?: unknown })?.value?.toString().trim();
    default:
      return value === undefined || value === null || value === "";
  }
};

const RequiredSuffix = () => (
  <Text
    component="span"
    size="xs"
    ml={8}
    fw={500}
    c="var(--mantine-color-error)"
  >
    Required
  </Text>
);

type ButtonSize = "tiny" | "small" | "medium" | "large";

/**
 * Legacy MUI grid columns (xs/sm/md/lg/xl) mapped onto Mantine Grid.Col
 * spans. Mantine's breakpoints sit close enough to the MUI ones for the
 * form layouts in use (12/6/4/3 splits at the md tier).
 */
const FormItemWrapper = ({
  children,
  item,
}: {
  children: JSX.Element;
  item?: FormInputItem;
}) => (
  <Grid.Col
    span={{
      base: item?.gridLayout?.xs ?? 12,
      sm: item?.gridLayout?.sm,
      md: item?.gridLayout?.md,
      lg: item?.gridLayout?.lg,
      xl: item?.gridLayout?.xl,
    }}
    style={{ display: "flex" }}
  >
    {children}
  </Grid.Col>
);

const SubmitButton = ({
  title,
  size,
  ...rest
}: { size: ButtonSize } & ButtonProps) => {
  switch (size) {
    case "small":
      return <SmallButton title={title} {...rest} />;

    default:
      return <Button title={title} {...rest} />;
  }
};

type FormProps = {
  loading?: boolean;
  message?: Message;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- form values are dynamically keyed; callers pass narrower types
  onSubmit?: (values: any) => void;
  inputs: FormInputItem[];
  buttonText?: string;
  buttonVariant?: "contained" | "outlined" | "text";
  buttonSx?: CSSProperties;
  buttonGridLayout?: {
    xs?: number;
    sm?: number;
    md?: number;
    lg?: number;
    xl?: number;
  };
  buttonDisabled?: boolean;
  extraButton?: ReactNode;
  itemSx?: CSSProperties;
  isHorizontal?: boolean;
  size?: "small" | "medium";
  fullWidth?: boolean;
  format?: string;
  buttonFullWidth?: boolean;
  buttonSize?: "small" | "medium" | "large";
  noValidation?: boolean;
  /**
   * Cross-field rules the per-field ValidationSchema cannot express (it keys
   * off field name alone, so a rule added there would apply to every form
   * carrying that field). Return `{ fieldName: message }`; schema errors win
   * on the same field. Runs on change, like the schema.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- form values are dynamically keyed
  validate?: (values: any) => Record<string, string> | undefined;
};

const Form = forwardRef(
  (
    {
      loading,
      message,
      onSubmit,
      inputs,
      buttonText,
      buttonVariant,
      buttonSx,
      buttonGridLayout,
      buttonDisabled,
      extraButton,
      itemSx,
      isHorizontal,
      size,
      fullWidth: _fullWidth = true,
      format,
      buttonFullWidth,
      buttonSize = "large",
      noValidation,
      validate,
    }: FormProps,
    ref,
  ) => {
    const getInitialValues = (empty = false) => {
      const values: LooseObject = {};
      if (inputs.length > 0) {
        for (let i = 0; i < inputs.length; i += 1) {
          switch (inputs[i].category) {
            case FormInputCategory.TEXT_FIELD:
              values[inputs[i].name] = empty
                ? ""
                : inputs[i].defaultValue || "";
              break;
            case FormInputCategory.CHECK_BOX:
              values[inputs[i].name] = empty
                ? false
                : inputs[i].defaultValue || false;
              break;
            case FormInputCategory.DATE_PICKER:
              values[inputs[i].name] = empty
                ? null
                : inputs[i].defaultValue || null;
              break;
            case FormInputCategory.DATE_RANGE_PICKER:
              values[`${inputs[i].name}From`] = empty
                ? null
                : (
                    inputs[i].defaultValue as
                      | { from?: unknown; to?: unknown }
                      | undefined
                  )?.from || null;
              values[`${inputs[i].name}To`] = empty
                ? null
                : (
                    inputs[i].defaultValue as
                      | { from?: unknown; to?: unknown }
                      | undefined
                  )?.to || null;
              break;
            case FormInputCategory.SELECT:
              values[inputs[i].name] = empty
                ? inputs[i].multiple
                  ? []
                  : ""
                : inputs[i].defaultValue === 0
                  ? 0
                  : inputs[i].defaultValue || (inputs[i].multiple ? [] : "");
              break;
            case FormInputCategory.AUTO_COMPLETE:
              values[inputs[i].name] = empty
                ? inputs[i].multiple
                  ? []
                  : ""
                : inputs[i].defaultValue || (inputs[i].multiple ? [] : "");
              break;
            default:
              values[inputs[i].name] = empty
                ? ""
                : inputs[i].defaultValue || "";
              break;
          }
        }
      }
      return values;
    };

    // useForm re-reads its options every render. Anything with unstable
    // identity (a fresh zod schema, a fresh defaultValues object) makes the
    // form update itself on each render and loops. So: defaultValues are
    // captured once at mount (same as Formik's initialValues without
    // enableReinitialize), and validation is a stable callback that reads
    // the CURRENT inputs through a ref (call sites rebuild `inputs` per
    // render, and some add fields dynamically).
    const inputsRef = useRef(inputs);
    inputsRef.current = inputs;
    const noValidationRef = useRef(noValidation);
    noValidationRef.current = noValidation;
    const validateRef = useRef(validate);
    validateRef.current = validate;

    const [initialValues] = useState(() => getInitialValues());

    const runValidation = useCallback(({ value }: { value: LooseObject }) => {
      if (noValidationRef.current) return undefined;
      const fields: Record<string, string> = {};
      const result = ValidationSchema(inputsRef.current).safeParse(value);
      if (!result.success) {
        for (const issue of result.error.issues) {
          const key = issue.path.join(".");
          if (!(key in fields)) fields[key] = issue.message;
        }
      }
      // Schema errors take precedence: "this field is required" is more
      // actionable than a cross-field rule about a value that isn't set yet.
      const extra = validateRef.current?.(value);
      if (extra) {
        for (const [key, message] of Object.entries(extra)) {
          if (!(key in fields)) fields[key] = message;
        }
      }
      return Object.keys(fields).length > 0 ? { fields } : undefined;
    }, []);

    const form = useForm({
      defaultValues: initialValues,
      validators: { onChange: runValidation },
      onSubmit: ({ value }) => {
        const optimisedValues = { ...value };
        Object.keys(optimisedValues).forEach((k) => {
          const input = inputs.find((i) => i.name === k);
          // remove SELECT_ALL_VALUE
          if (input?.multiple && input?.selectAll) {
            optimisedValues[k] = (optimisedValues[k] as string[]).filter(
              (i: string) => i !== SELECT_ALL_VALUE,
            );
          }
        });
        onSubmit?.(optimisedValues);
      },
    });

    useImperativeHandle(ref, () => ({
      resetForm: () => {
        form.reset(getInitialValues(true));
      },
    }));

    const setFieldValue = (name: string, value: unknown) =>
      form.setFieldValue(name, value);

    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
        style={
          isHorizontal
            ? {
                position: "relative",
                display: "flex",
                flexDirection: "row",
                width: "100%",
              }
            : { textAlign: "center", width: "100%" }
        }
      >
        {inputs && Array.isArray(inputs) && inputs.length > 0 && (
          <form.Subscribe selector={(state) => state.values}>
            {(values) => (
              <Grid
                w="100%"
                gap={inputs.length > 1 ? 16 : 0}
                style={{ textAlign: "left" }}
                align={isHorizontal ? "flex-end" : undefined}
              >
                {inputs
                  .filter((item) => !item.hidden)
                  .map((rawItem) => {
                    let item: FormInputItem = rawItem.disabledWhen
                      ? { ...rawItem, disabled: rawItem.disabledWhen(values) }
                      : rawItem;
                    if (item.size === undefined && size !== undefined) {
                      item = { ...item, size };
                    }
                    if (
                      item.required &&
                      isFieldEmpty(item.category, values[item.name])
                    ) {
                      item = {
                        ...item,
                        label: (
                          <>
                            {item.label}
                            <RequiredSuffix />
                          </>
                        ),
                      };
                    }

                    switch (item.category) {
                      case FormInputCategory.COMPONENT:
                        return (
                          <FormItemWrapper key={item.name} item={item}>
                            <Box w="100%">{item.component}</Box>
                          </FormItemWrapper>
                        );

                      case FormInputCategory.COMPONENT_FUNCTION:
                        return (
                          <FormItemWrapper key={item.name} item={item}>
                            <Box w="100%">
                              {item.componentFunction &&
                                item.componentFunction({ values })}
                            </Box>
                          </FormItemWrapper>
                        );

                      case FormInputCategory.DATE_PICKER:
                        return (
                          <FormItemWrapper key={item.name} item={item}>
                            <DatePicker
                              item={item}
                              values={values}
                              setFieldValue={setFieldValue}
                              textFieldProps={{ sx: itemSx }}
                              format={format}
                            />
                          </FormItemWrapper>
                        );

                      case FormInputCategory.DATE_RANGE_PICKER:
                        return (
                          <FormItemWrapper key={item.name} item={item}>
                            <DateRangePicker
                              item={item}
                              values={values}
                              setFieldValue={setFieldValue}
                              textFieldProps={{ sx: itemSx }}
                              format={format}
                            />
                          </FormItemWrapper>
                        );

                      default: {
                        const Renderer =
                          item.category === FormInputCategory.TEXT_FIELD
                            ? FormTextField
                            : item.category === FormInputCategory.SELECT
                              ? FormSelect
                              : item.category ===
                                  FormInputCategory.AUTO_COMPLETE
                                ? FormAutocomplete
                                : item.category === FormInputCategory.CHECK_BOX
                                  ? FormCheckbox
                                  : item.category ===
                                      FormInputCategory.RADIO_GROUP
                                    ? FormRadioGroup
                                    : null;
                        if (!Renderer) {
                          return (
                            <FormItemWrapper key={item.name} item={item}>
                              <Box />
                            </FormItemWrapper>
                          );
                        }
                        const renderItem = item;
                        return (
                          <FormItemWrapper key={item.name} item={item}>
                            <form.Field name={item.name}>
                              {(field) => (
                                <Renderer
                                  item={renderItem}
                                  field={field}
                                  style={itemSx}
                                />
                              )}
                            </form.Field>
                          </FormItemWrapper>
                        );
                      }
                    }
                  })}

                {buttonText && (
                  <FormItemWrapper
                    key={"submit_button"}
                    item={
                      {
                        name: "",
                        gridLayout: buttonGridLayout,
                      } as FormInputItem
                    }
                  >
                    <Flex
                      direction="row"
                      // Extra buttons wrap below the submit button instead of
                      // clipping their labels when the row is wider than a
                      // narrow dialog (e.g. the GMP booking editor).
                      wrap="wrap"
                      gap={16}
                      w={
                        (
                          buttonFullWidth !== undefined
                            ? buttonFullWidth
                            : isHorizontal
                              ? undefined
                              : true
                        )
                          ? "100%"
                          : undefined
                      }
                      my={
                        isHorizontal
                          ? undefined
                          : buttonSize === "large"
                            ? 24
                            : buttonSize === "small"
                              ? 8
                              : 16
                      }
                    >
                      <form.Subscribe selector={(state) => state.isValid}>
                        {(isValid) => (
                          <SubmitButton
                            title={buttonText}
                            loading={loading}
                            variant={buttonVariant || "contained"}
                            type="submit"
                            disabled={!isValid || buttonDisabled}
                            style={buttonSx}
                            size={buttonSize}
                            fullWidth={
                              buttonFullWidth !== undefined
                                ? buttonFullWidth
                                : !isHorizontal
                            }
                          />
                        )}
                      </form.Subscribe>
                      {extraButton}
                      <form.Subscribe selector={(state) => state.isValid}>
                        {(isValid) => {
                          // A disabled submit button with the culprit fields
                          // scrolled out of view reads as "the form is
                          // broken" (long forms like the GMP supply
                          // agreement) — say why it is disabled. Only shown
                          // once validation has run, so pristine forms don't
                          // open with a warning.
                          if (isValid) return null;
                          const missingRequired = inputs.filter(
                            (item) =>
                              !item.hidden &&
                              item.required &&
                              isFieldEmpty(item.category, values[item.name]),
                          ).length;
                          if (!missingRequired) return null;
                          return (
                            <Text
                              w="100%"
                              size="xs"
                              ta="left"
                              c="var(--mantine-color-error)"
                            >
                              {missingRequired === 1
                                ? "1 required field is incomplete"
                                : `${missingRequired} required fields are incomplete`}
                            </Text>
                          );
                        }}
                      </form.Subscribe>
                    </Flex>
                  </FormItemWrapper>
                )}
                {message?.text ? <ServerMessage message={message} /> : null}
              </Grid>
            )}
          </form.Subscribe>
        )}
      </form>
    );
  },
);

export default Form;
